import type { MouseEvent } from 'react';
import { PictureInPictureIcon } from '@phosphor-icons/react';
import { AnimateIcon } from '@/components/animate-ui/icons/animate-icon';
import { AudioLines } from '@/components/animate-ui/icons/audio-lines';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

export type ThumbnailQuickViewState = 'idle' | 'watching';

interface QuickViewOverlayProps {
    state: ThumbnailQuickViewState;
    onClick?: (event: MouseEvent) => void;
    ariaLabel: string;
    compact?: boolean;
}

export function QuickViewOverlay({
    state,
    onClick,
    ariaLabel,
    compact = false,
}: QuickViewOverlayProps) {
    const iconSize = compact ? 20 : 24;
    const buttonSizeClassName = compact ? 'h-5 w-5' : 'h-6 w-6';

    if (state === 'watching') {
        return (
            <>
                <div
                    className="pointer-events-none absolute inset-0 z-[24] bg-[rgba(17,18,20,0.7)]"
                    aria-hidden
                />
                <div className="pointer-events-none absolute inset-0 z-[25] flex items-center justify-center text-white-100">
                    <AnimateIcon animate loop>
                        <AudioLines
                            size={iconSize}
                            className="shrink-0 text-white-100"
                        />
                    </AnimateIcon>
                </div>
                <div
                    className={cn(
                        'pointer-events-none absolute bottom-1 right-1 z-[23] flex items-center justify-center text-white-100',
                        buttonSizeClassName,
                    )}
                >
                    <PictureInPictureIcon size={iconSize} weight="regular" />
                </div>
            </>
        );
    }

    return (
        <>
            <div
                className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-[linear-gradient(180deg,rgba(17,18,20,0)_0%,rgba(17,18,20,0.18)_45%,rgba(17,18,20,0.72)_100%)] opacity-0 transition-opacity group-hover/thumbnail:opacity-100"
                aria-hidden
            />
            <div className="absolute bottom-1 right-1 opacity-0 transition-opacity group-hover/thumbnail:opacity-100">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            type="button"
                            aria-label={ariaLabel}
                            onClick={(e) => {
                                e.stopPropagation();
                                onClick?.(e);
                            }}
                            className={cn(
                                'flex items-center justify-center text-white-100 transition-colors hover:text-grey-50',
                                buttonSizeClassName,
                            )}
                        >
                            <PictureInPictureIcon
                                size={iconSize}
                                weight="regular"
                            />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" sideOffset={8}>
                        {ariaLabel}
                    </TooltipContent>
                </Tooltip>
            </div>
        </>
    );
}
